import * as moment from "moment";
import { createRef, FC, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import { B2Button } from "@hdb/biome-2";
import EnergyStackedChartComponent from "../../../shared/custom-highchart-component/energy-stacked-chart.component";
import { DAY, MONTH } from "src/shared/helpers/current-month";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";
import { assetsByBlock } from "src/store/slices/asset.slice";
import pdfIcon from "../../../assets/images/pdfBiomeIcon.svg";
import spreadsheetIcon from "../../../assets/images/spreadsheetIconBiome.svg";
import HasPermission from "../../../core/guards/has-permission";
import CustomHighChartComponent from "../../../shared/custom-highchart-component/custom-highchart.component";
import CustomSpinnerComponent from "../../../shared/custom-spinner/custom-spinner.component";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../shared/filter-form/filter-form.component";
import { downloadEnergyReportsFile } from "../../../store/slices/fault-module/energy-report.slice";
import { getBlocksBySiteCode } from "../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../store/slices/town-council.slice";
import { EnergyConsumptionChartType } from "src/core/enums/dashboard-enums";
import CustomSplineChartComponent from "src/shared/custom-highchart-component/custom-spline-chart.component";
interface EnergyReportTableComponentProps {
  chartData: { timePeriods: any[]; energyAmounts: any[] };
  energyConsumptionVsTimeData: { energyConsumptionInWKH: any[]; savedEnergy: any[]; timeperiod: any[], energyConsumptionBaseline: any[] };
  energyConsumptionVsLevelAndLocationData: { seriesData: any[]; xAxisValues: any[] };
  energyConsumptionVsSensorData: { energyConsumed: any[]; sensorNode: any[]; extraData: any[] };
  onRefetchData: (
    tcCode: any,
    siteCode: any,
    blockCode: any,
    reportType: any,
    energyConsumptionChartType: any,
    startDate: any,
    endDate: any,
    timePeriod: any,
    asset: any,
    energyRate: any
  ) => void;
  onResetForm: () => void;
  isLoading: boolean;
}

const EnergyReportTableComponent: FC<EnergyReportTableComponentProps> = ({
  chartData,
  energyConsumptionVsTimeData,
  energyConsumptionVsLevelAndLocationData,
  energyConsumptionVsSensorData,
  onRefetchData,
  onResetForm,
  isLoading,
}) => {
  const formRef = createRef();
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [selectedReportType, setSelectedReportType] = useState<any>(null);
  const [showReportType, setShowReportType] = useState<any>(null);
  const [showEnergyConsumptionChartType, setShowEnergyConsumptionChartType] = useState<any>(null);
  const [assetOptions, setAssetOptions] = useState<any>([]);
  const [isFiltered, setIsFiltered] = useState<any>(false);
  const [isHideVisibility, setIsHideVisibility] = useState<any>(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<any>(null);
  const [selectedEnergyConsumptionChartType, setSelectedEnergyConsumptionChartType] =
    useState<any>(null);
  const [visibilityConditions, setVisibilityConditions] = useState<any>({
    reportType: false,
    energyConsumptionChartType: false,
    startDate: false,
    endDate: false,
    startMonth: false,
    endMonth: false,
  });
  const [selectedTC, setSelectedTC] = useState<any>(null);
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [selectedBlock, setSelectedBlock] = useState<any>(null);
  const [blockChangeDetected, setBlockChangeDetected] = useState(0);

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    energyConsumptionChartType: "",
    startDate: "",
    endDate: "",
    timePeriod: "",
    asset: "",
    energyRate: "",
  });

  const [downloaDocumentPayload, setDownloaDocumentPayload] = useState<any>({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    energyConsumptionChartType: "",
    startDate: "",
    endDate: "",
    timePeriod: "",
    asset: "",
    energyRate: "",
  });
  const [isAssetSearch, setIsAssetSearch] = useState<boolean>(true);

  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const reportTypeOptions = [
    {
      label: "Energy Consumption",
      value: 1,
    },
    {
      label: "CO2 Saving",
      value: 3,
    },
    {
      label: "Financial Saving",
      value: 4,
    },
  ];

  const timePeriodOptions = [
    {
      label: "Daily",
      value: 1,
    },
    {
      label: "Monthly",
      value: 2,
    },
  ];

  const energyConsumptionChartOptions = [
    { label: "Energy vs Time", value: 1 },
    { label: "Energy vs Level", value: 2 },
    { label: "Energy vs Location", value: 3 },
    { label: "Energy vs Sensor", value: 4 },
  ];

  const requiresEndDate = (reportType: any, chartType: any) =>
    String(reportType) !== "1" ||
    !chartType ||
    Number(chartType) === EnergyConsumptionChartType.Energy_Vs_Time;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const isLoadingEnergyReportsDownload = useSelector((state: any) => {
    return state.energyReport.isLoadingEnergyReportsDownload;
  });

  const assetsData = useSelector((state: any) => {
    return state.asset.selectedAssetsByBlock;
  });

  useEffect(() => {
    const assetIDs =
      assetsData?.map(
        (asset: { assetCode: string; sensorDisplayName: string }) => ({
          label: asset.sensorDisplayName,
          value: asset.assetCode,
        })
      ) || [];

    if (assetIDs.length > 0) {
      assetIDs.unshift({
        label: "All",
        value: null,
      });
    }

    setAssetOptions(assetIDs);

    if (assetsData?.length > 0) {
      setIsAssetSearch(false);
    }
  }, [assetsData]);

  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
  }, []);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);
    setAssetOptions([]);
    setSelectedTC(selectedTC);
    setBlockChangeDetected(blockChangeDetected + 1);

    const form: any = formRef.current;

    if (form) {
      // Get all field names except 'towncouncil'
      const fieldNames = Object.keys(form.values).filter(
        (name) => name !== "towncouncil"
      );

      // Reset each field to empty, except 'towncouncil'
      fieldNames.forEach((fieldName) => {
        if (fieldName === "site" || fieldName === "block") {
          form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
        }
      });
    }

    if (!selectedTC) return;

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
      asset: "",
    }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);
    setAssetOptions([]);
    setSelectedSite(selectedSite);
    setBlockChangeDetected(blockChangeDetected + 1);

    if (!selectedSite) return;

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      site: selectedSite,
      block: "",
      asset: "",
    }));
  };

  useEffect(() => {
    if (selectedTC && selectedSite && selectedBlock) {
      dispatch(assetsByBlock({ blockCode: selectedBlock }));
    }
  }, [selectedBlock]);

  const onChangeReportType = (reportType: any) => {
    setSelectedReportType(reportType);
    setSelectedEnergyConsumptionChartType(null);

    setVisibilityConditions({
      reportType: Boolean(reportType),
      startDate: Boolean(visibilityConditions?.startDate),
      endDate: Boolean(visibilityConditions?.endDate),
    });

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      reportType: reportType,
      energyRate: "",
      energyConsumptionChartType:
        String(reportType) === "1" ? prevInitValues.energyConsumptionChartType : "",
    }));
  };

  const onChangeEnergyConsumptionChartType = (energyConsumptionChartType: any) => {
    setSelectedEnergyConsumptionChartType(energyConsumptionChartType);

    setVisibilityConditions({
      energyConsumptionChartType: Boolean(energyConsumptionChartType),
      startDate: Boolean(visibilityConditions?.startDate),
      endDate: requiresEndDate("1", energyConsumptionChartType)
        ? visibilityConditions?.endDate
        : false,
    });

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      energyConsumptionChartType: energyConsumptionChartType,
      ...(!requiresEndDate("1", energyConsumptionChartType)
        ? { endDate: "" }
        : {}),
    }));
  };

  const onChangeStartDate = (startDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      startDate: startDate,
    }));

    setVisibilityConditions({
      reportType: Boolean(visibilityConditions?.reportType),
      startDate: Boolean(startDate),
      endDate: Boolean(visibilityConditions?.endDate),
    });
  };

  const onChangeEndDate = (endDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      endDate: endDate,
    }));

    setVisibilityConditions({
      reportType: Boolean(visibilityConditions?.reportType),
      startDate: Boolean(visibilityConditions?.startDate),
      endDate: Boolean(endDate),
    });
  };

  const onChangeTimePeriod = (timePeriod: any) => {
    setSelectedTimePeriod(timePeriod);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      timePeriod: timePeriod.toString(),
      startDate: "",
      endDate: "",
    }));
  };

  useEffect(() => {
    return () => {
      setSelectedReportType(null);
      setShowReportType(null);
      setShowEnergyConsumptionChartType(null);
    };
  }, []);

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      const {
        townCouncil,
        site,
        block,
        reportType,
        energyConsumptionChartType,
        startDate,
        endDate,
        timePeriod,
        asset,
        energyRate,
      } = form.values;

      if (endDate) {
        if (endDate < startDate) {
          setIsFiltered(false);
          return;
        }
      }
      if (!energyRate && selectedReportType === "4") return;

      const endDateRequired = requiresEndDate(
        reportType,
        energyConsumptionChartType
      );

      if (
        townCouncil &&
        site &&
        block &&
        reportType &&
        startDate &&
        (!endDateRequired || endDate) &&
        timePeriod
      ) {
        setSelectedReportType(reportType);
        setShowReportType(reportType);

        if (reportType == 1 && !energyConsumptionChartType) {
          return;
        } else if (reportType == 1 && energyConsumptionChartType) {
          setShowEnergyConsumptionChartType(energyConsumptionChartType);
        }

      } else {
        return;
      }

      onRefetchData(
        townCouncil || null,
        site || null,
        block || null,
        reportType || null,
        energyConsumptionChartType || null,
        startDate ? moment.utc(startDate) : null,
        endDate ? moment.utc(endDate) : null,
        timePeriod || "",
        asset || null,
        energyRate || null
      );

      setDownloaDocumentPayload({
        townCouncil,
        site,
        block,
        reportType,
        energyConsumptionChartType,
        startDate,
        endDate,
        timePeriod,
        asset,
        energyRate,
      });

      setIsFiltered(true);
    }
  };

  const onClickDownload = (fileType: any) => {
    const form: any = formRef.current;

    if (form) {
      // form.handleSubmit();

      const {
        townCouncil,
        site,
        block,
        reportType,
        energyConsumptionChartType,
        startDate,
        endDate,
        timePeriod,
        asset,
        energyRate,
      } = downloaDocumentPayload;

      if (
        !townCouncil ||
        !site ||
        !block ||
        !reportType ||
        (reportType == 1 && !energyConsumptionChartType) ||
        !startDate ||
        (requiresEndDate(reportType, energyConsumptionChartType) && !endDate) ||
        !timePeriod
      ) {
        return;
      }

      dispatch(
        downloadEnergyReportsFile({
          fileType,
          tcCode: townCouncil || null,
          siteCode: site || null,
          blockCode: block || null,
          reportType: reportType || null,
          energyConsumptionChartType: energyConsumptionChartType || null,
          startDate: startDate ? moment.utc(startDate) : null,
          endDate: endDate ? moment.utc(endDate) : null,
          timePeriod: timePeriod || "",
          asset: asset || null,
          energyRate: energyRate || null,
        })
      );
    }
  };

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setFilterUpdateCount(filterUpdateCount + 1);

    setIsFiltered(false);
    setSelectedReportType(null);
    setShowReportType(null);
    setShowEnergyConsumptionChartType(null);
    setSelectedEnergyConsumptionChartType(null);

    setIsHideVisibility(true);
    setSelectedTimePeriod(null);
    setVisibilityConditions({
      reportType: false,
      energyConsumptionChartType: false,
      startDatte: false,
      endDate: false,
      startMonth: false,
      endMonth: false,
    });

    setSiteOptions([]);
    setBlockOptions([]);

    setIsFiltered(false);
    setIsAssetSearch(true);
    setSelectedBlock(null);
    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      reportType: "",
      energyConsumptionChartType: "",
      startDate: "",
      endDate: "",
      timePeriod: "",
      asset: "",
      energyRate: "",
    });

    onResetForm();
  };

  const filterActionConfig: any = {
    label: "Search",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const onChangeBlock = (selectedBlock: any) => {
    setAssetOptions([]);
    setSelectedBlock(selectedBlock);
    setBlockChangeDetected(blockChangeDetected + 1);
    if (!selectedBlock) return;

    // onChangeLocation(selectedBlock);
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      block: selectedBlock,
      asset: "",
    }));
  };

  const onChangeAsset = (selectedAsset: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      asset: selectedAsset,
    }));
  };
  const onChangeEnergyRate = (energyRate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      energyRate: energyRate,
    }));
  };

  // const yesterdayDateTime = moment().subtract(1, "day").format("YYYY-MM-DD");

  const date13MonthsAgo = moment().subtract(13, "months").format("YYYY-MM-DD");
  const yesterdayDateTime = moment().subtract(1, "day").format("YYYY-MM-DD");

  const thirteenMonthsAgo = moment().subtract(13, "months").format("YYYY-MM");
  const thisMonth = moment().format("YYYY-MM");

  const [filterFormConfig, setFilterFormConfig] = useState<FilterFormConfig>({
    controls: [],
  });

  useEffect(() => {
    setFilterFormConfig({ controls: [] });

    const controls = [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "searchable-select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site *",
            type: "searchable-select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block *",
            type: "searchable-select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "reportType",
            placeholder: "Select Report Type *",
            type: "select",
            options: reportTypeOptions,
            onChange: onChangeReportType,
            required: true,
            isLoading: false,
          },
        ],
      },
    ];

    let dateControls: any = [];
    let monthControls: any = [];
    let assetControl: any = [];
    let financialControl: any = [];
    let energyConsumptionChartControl: any = [];
    let timePeriodControl: any = [];
    const showEndDateControl = requiresEndDate(
      selectedReportType,
      selectedEnergyConsumptionChartType
    );

    if (String(selectedReportType) === "1") {
      energyConsumptionChartControl = [
        {
          controlGroup: [
            {
              name: "energyConsumptionChartType",
              placeholder: "Select Consumption Cat. *",
              type: "select",
              options: energyConsumptionChartOptions,
              onChange: onChangeEnergyConsumptionChartType,
              required: true,
              isLoading: false,
            },
          ],
        },
      ];
    }

    if (selectedReportType) {
      timePeriodControl = [
        {
          controlGroup: [
            {
              name: "timePeriod",
              placeholder: "Select Time Period *",
              type: "select",
              options: timePeriodOptions,
              onChange: onChangeTimePeriod,
              required: true,
              isLoading: false,
            },
          ],
        },
      ];
    }

    if (selectedTimePeriod === DAY) {
      dateControls = [
        {
          controlGroup: [
            {
              name: "startDate",
              placeholder: "Select Start Date *",
              type: "date",
              onChange: onChangeStartDate,
              required: true,
              isLoading: false,

              minDate: date13MonthsAgo,
              maxDate: yesterdayDateTime,
              hideVisibility: isHideVisibility,
            },
          ],
        },
        ...(showEndDateControl
          ? [
            {
              controlGroup: [
                {
                  name: "endDate",
                  placeholder: "Select End Date*",
                  type: "date",
                  onChange: onChangeEndDate,
                  required: true,
                  isLoading: false,
                  minDate: date13MonthsAgo,
                  maxDate: yesterdayDateTime,
                  hideVisibility: isHideVisibility,
                },
              ],
            },
          ]
          : []),
      ];
    } else if (selectedTimePeriod === MONTH) {
      monthControls = [
        {
          controlGroup: [
            {
              name: "startDate",
              placeholder: "Select Start Month*",
              type: "month",
              onChange: onChangeStartDate,
              required: true,
              isLoading: false,
              minDate: thirteenMonthsAgo,
              maxDate: thisMonth,
              hideVisibility: isHideVisibility,
            },
          ],
        },
        ...(showEndDateControl
          ? [
            {
              controlGroup: [
                {
                  name: "endDate",
                  placeholder: "Select End Month*",
                  type: "month",
                  onChange: onChangeEndDate,
                  required: true,
                  isLoading: false,
                  minDate: thirteenMonthsAgo,
                  maxDate: thisMonth,
                  hideVisibility: isHideVisibility,
                },
              ],
            },
          ]
          : []),
      ];
    }

    if (assetOptions?.length) {
      assetControl = [
        {
          controlGroup: [
            {
              name: "asset",
              placeholder: "All Assets",
              type: "searchable-select",
              required: false,
              selectType: "single",
              onChange: onChangeAsset,
              options: assetOptions,
              isLoading: true,
              disabled: isAssetSearch,
              resetSearchableSelect: blockChangeDetected,
            },
          ],
        },
      ];
    }

    if (String(selectedReportType) === "4") {
      financialControl = [
        {
          controlGroup: [
            {
              name: "energyRate",
              placeholder: "Electricity Tariff (cents/KWh)*",
              type: "number",
              required: true,
              onChange: onChangeEnergyRate,
              className: "form-control-wrapper",
            },
          ],
        },
      ];
    }

    const updatedControls = [
      ...controls,
      ...energyConsumptionChartControl,
      ...timePeriodControl,
      ...dateControls,
      ...monthControls,
      ...assetControl,
      ...financialControl,
    ];

    setFilterFormConfig({
      controls: updatedControls,
    });
  }, [
    selectedReportType,
    selectedEnergyConsumptionChartType,
    selectedTimePeriod,
    siteOptions,
    tcOptions,
    assetOptions,
    blockOptions,
  ]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
    setAssetOptions([]);
    setIsAssetSearch(true);
  }, []);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const validationSchema = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
    site: yup.string().required("Site is required"),
    block: yup.string().required("Block is required"),
    reportType: yup.number().required("Report Type is required"),
    energyConsumptionChartType: yup.string().when("reportType", {
      is: (reportType: any) => String(reportType) === "1",
      then: (schema) =>
        schema.required("Energy Consumption Chart Type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    energyRate: yup
      .number()
      .required("Electricity Tariff (cents/KWh) is required"),
    startDate: yup.date().required(function (value) {
      return selectedTimePeriod === MONTH
        ? "Start Month is required"
        : "Start Date is required";
    }),
    endDate: yup
      .date()
      .when(["reportType", "energyConsumptionChartType"], {
        is: (reportType: any, chartType: any) =>
          !requiresEndDate(reportType, chartType),
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.required(function () {
            return selectedTimePeriod === MONTH
              ? "End Month is required"
              : "End Date is required";
          }),
      })
      .test("end-after-start", function (value, context) {
        const { startDate } = context?.parent || {};

        if (!value || !startDate) return true;

        const errorText =
          selectedTimePeriod === MONTH
            ? "End Month must be after Start Month"
            : "End Date must be after Start Date";

        return (
          new Date(value) >= new Date(startDate) ||
          context.createError({ message: errorText })
        );
      }),

    timePeriod: yup.number().required("Time Period is required"),
  });

  const getEnergyConsumptionXaxisTitle = () => {
    if (visibilityConditions.reportType && visibilityConditions.endDate) {
      if (selectedTimePeriod == DAY) {
        return "Time (Date)";
      } else if (selectedTimePeriod == MONTH) {
        return "Time (Months)";
      }
    } else {
      return "Time (Hours)";
    }
  };

  const energyReportToolTipFormatter = (point: any) => {
    const { x, y } = point;

    // Generic handler for energy-related metrics
    const energyMetrics = [
      "Energy Consumptio(Wh)",
      "Energy Saving(Wh)",
      "CO2 Saving(kg)",
      "Financial Saving(SGD)",
    ];

    const time = chartData?.timePeriods?.[point.index] || point.category || x;

    const TimeDate = `<b>Time (Date):</b> ${time}<br />`;
    const MetricValue = `<b>${energyMetrics[Number(selectedReportType) - 1]
      }:</b> ${y}<br />`;

    return `${TimeDate}${MetricValue}`;
  };

  const renderedChart = (() => {
    const reportType = Number(showReportType);

    switch (reportType) {
      case 1:
        return showEnergyConsumptionChartType == EnergyConsumptionChartType.Energy_Vs_Time ? (
          <CustomSplineChartComponent
            xAxisTitleText="Time (Date)"
            yAxisTitleText="Energy Consumption(Wh)"
            seriesData={energyConsumptionVsTimeData?.energyConsumptionInWKH}
            baseline={energyConsumptionVsTimeData?.energyConsumptionBaseline}
            xAxisCategories={energyConsumptionVsTimeData?.timeperiod}
            savedEnergy={energyConsumptionVsTimeData?.savedEnergy}
            fileName={generateFilenameWithTimestamp(
              "Energy Consumption"
            )}
            chartName={"Energy Consumption"}
          />
        ) : showEnergyConsumptionChartType == EnergyConsumptionChartType.Energy_Vs_Level ? (
          <EnergyStackedChartComponent
            xAxisCategories={energyConsumptionVsLevelAndLocationData?.xAxisValues}
            SeriesType={"column"}
            seriesData={energyConsumptionVsLevelAndLocationData?.seriesData}
            seriesShowInLegend={false}
            isShowDataLevels={false}
            isShowStackedColor={false}
            seriesName="Energy Consumption(Wh)"
            xAxisTitleText={"Level"}
            yAxisTitleText="Energy Consumption(Wh)"
            tooltipPrefix={"Level:"}
          />
        ) : showEnergyConsumptionChartType == EnergyConsumptionChartType.Energy_Vs_Location ? (
          <EnergyStackedChartComponent
            xAxisCategories={energyConsumptionVsLevelAndLocationData?.xAxisValues}
            SeriesType="column"
            seriesData={energyConsumptionVsLevelAndLocationData?.seriesData}
            seriesShowInLegend={false}
            isShowDataLevels={false}
            isShowStackedColor={false}
            seriesName="Energy Consumption(Wh)"
            xAxisTitleText={"Location"}
            yAxisTitleText="Energy Consumption(Wh)"
            tooltipPrefix={"Location:"}
          />
        ) : showEnergyConsumptionChartType == EnergyConsumptionChartType.Energy_Vs_Sensor ? (
          <CustomHighChartComponent
            xAxisCategories={
              energyConsumptionVsSensorData?.sensorNode
            }
            SeriesType={"column"}
            seriesData={energyConsumptionVsSensorData?.energyConsumed}
            seriesShowInLegend={false}
            seriesName={"Energy Consumption(Wh)"}
            xAxisTitleText={"Sensors"}
            yAxisTitleText={"Energy Consumption(Wh)"}
            colors={["#66b2ff"]}
            extraData={energyConsumptionVsSensorData?.extraData}
            redirectRoute="/asset/"
            isEnergyTooltip={true}
          />
        ) : null;
      case 3:
        return (
          <CustomHighChartComponent
            chartTitle="CO2 Saving"
            xAxisCategories={chartData?.timePeriods}
            SeriesType={"spline"}
            seriesData={chartData?.energyAmounts}
            seriesShowInLegend={false}
            seriesName={"CO2 Saving"}
            xAxisTitleText={getEnergyConsumptionXaxisTitle()}
            yAxisTitleText={"CO2 Saving(kg)"}
            colors={["#2caffe"]}
            // baseLine={[40000]}
            fileName={generateFilenameWithTimestamp("CO2 Saving")}
            chartName={"CO2 Saving"}
            tooltipFormatter={energyReportToolTipFormatter}
          />
        );
      default:
        return (
          <CustomHighChartComponent
            chartTitle="Financial Saving"
            xAxisCategories={chartData?.timePeriods}
            SeriesType={"spline"}
            seriesData={chartData?.energyAmounts}
            seriesShowInLegend={false}
            seriesName={"Financial Saving"}
            xAxisTitleText={getEnergyConsumptionXaxisTitle()}
            yAxisTitleText={"Financial Saving(SGD)"}
            colors={["#2caffe"]}
            // baseLine={[40000]}
            fileName={generateFilenameWithTimestamp("Financial Saving")}
            chartName={"Financial Saving"}
            tooltipFormatter={energyReportToolTipFormatter}
          />
        );
    }
  })();

  return (
    <div>
      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          validation={validationSchema}
          filterUpdateCount={filterUpdateCount}
        />
      </div>

      <div>
        <CustomSpinnerComponent
          height={"400px"}
          width={"100%"}
          isLoading={isLoading}
        >
          {selectedReportType && isFiltered && (
            <div className="my-3">
              <div className="export pt-4 d-flex align-items-center justify-content-end">
                <HasPermission
                  moduleName="Energy Report"
                  actionName="Download-Energy Report"
                  isRoute={false}
                >
                  <div className="d-flex gap-2">
                    <B2Button
                      variant="light"
                      onClick={() => onClickDownload("downloadPdf")}
                      disabled={
                        !(
                          chartData?.energyAmounts?.length ||
                          energyConsumptionVsTimeData?.energyConsumptionInWKH?.length ||
                          energyConsumptionVsLevelAndLocationData?.seriesData?.length ||
                          energyConsumptionVsSensorData?.energyConsumed?.length
                        )
                      }
                    >
                      <img className="fs-7 ms-2" src={pdfIcon} alt="PDF Icon" />{" "}
                      Export as PDF
                    </B2Button>

                    <B2Button
                      variant="light"
                      onClick={() => onClickDownload("downloadExcel")}
                      disabled={
                        !(
                          chartData?.energyAmounts?.length ||
                          energyConsumptionVsTimeData?.energyConsumptionInWKH?.length ||
                          energyConsumptionVsLevelAndLocationData?.seriesData?.length ||
                          energyConsumptionVsSensorData?.energyConsumed?.length
                        )
                      }
                    >
                      <img
                        className="fs-7 me-2"
                        src={spreadsheetIcon}
                        alt="Spreadsheet Icon"
                      />{" "}
                      Export as Excel
                    </B2Button>
                  </div>

                  {isLoadingEnergyReportsDownload ? (
                    <Spinner
                      className="ms-2"
                      style={{ color: "#1976D2" }}
                      animation="border"
                      role="status"
                      size="sm"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  ) : null}
                </HasPermission>
              </div>
              {renderedChart}
            </div>
          )}
        </CustomSpinnerComponent>
      </div>
    </div>
  );
};

export default EnergyReportTableComponent;
